import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { formatDate, formatNumber } from "utils/format";

/** ArenaSeasonManager.KIND_RANK / KIND_POINTS */
const KIND_NAMES = { 0: "Thưởng hạng", 1: "Mốc tham gia" };
const STATUS_COLORS = { 0: "success", 1: "warning", 2: "info" };

/** Thêm / sửa một mốc thưởng mùa lôi đài */
function SeasonTierDialog({ tier, kind, nextFrom, onClose, onDone }) {
  const [run, busy] = useRunner();
  const tierKind = tier ? tier.kind : kind;
  const isPoints = tierKind === 1;
  const [form, setForm] = useState(() =>
    tier
      ? { rankFrom: tier.rankFrom, rankTo: tier.rankTo, gift: tier.gift, enabled: tier.enabled }
      : { rankFrom: isPoints ? 60 : nextFrom, rankTo: nextFrom, gift: [[18, 10]], enabled: true }
  );
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    const body = {
      kind: tierKind,
      rankFrom: Number(form.rankFrom),
      rankTo: isPoints ? 0 : Number(form.rankTo),
      gift: form.gift,
      enabled: Boolean(form.enabled),
    };
    const { ok } = await run(
      () =>
        tier
          ? api.put(`/api/admin/arena-event/season/rewards/${tier.id}`, body)
          : api.post("/api/admin/arena-event/season/rewards", body),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      maxWidth="md"
      title={`${tier ? "Sửa" : "Thêm"} ${KIND_NAMES[tierKind].toLowerCase()}`}
      description={
        isPoints
          ? "Ai có điểm mùa từ mức này trở lên đều nhận (lấy mốc cao nhất đạt được), cộng chung thư với thưởng hạng. Quà đã chốt được lưu riêng, sửa mốc chỉ áp dụng cho lần chốt sau."
          : "Hạng là hạng trên BXH mùa lôi đài lúc chốt. Người chưa đủ số trận tối thiểu không nhận thưởng hạng và không đẩy người sau lên. Danh hiệu có hạn tự đổi thành hạn tới hết mùa sau lúc chốt."
      }
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label={isPoints ? "Điểm mùa tối thiểu" : "Từ hạng"}
            value={form.rankFrom}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ rankFrom: e.target.value })}
          />
        </Grid>
        {!isPoints && (
          <Grid item xs={6} md={4}>
            <MDInput
              fullWidth
              type="number"
              label="Đến hạng (0 = trở xuống hết)"
              value={form.rankTo}
              inputProps={{ min: 0 }}
              onChange={(e) => set({ rankTo: e.target.value })}
            />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <MDBox display="flex" alignItems="center" height="100%">
            <Switch
              checked={Boolean(form.enabled)}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <MDTypography variant="caption">Đang áp dụng</MDTypography>
          </MDBox>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <GiftEditor
        value={form.gift}
        onChange={(gift) => set({ gift })}
        label="Phần thưởng gửi vào thư"
        randomNote="Thưởng mùa nên dùng quà cố định để người chơi biết trước mình nhận gì."
      />
    </ActionDialog>
  );
}

SeasonTierDialog.defaultProps = { tier: null, kind: 0, nextFrom: 1 };

SeasonTierDialog.propTypes = {
  tier: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.number,
    rankFrom: PropTypes.number,
    rankTo: PropTypes.number,
    gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    enabled: PropTypes.bool,
  }),
  kind: PropTypes.number,
  nextFrom: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function TiersCard({ tiers, canEdit, onChanged }) {
  const [kind, setKind] = useState(0);
  const [editing, setEditing] = useState(null);
  const [run] = useRunner();
  const confirm = useConfirm();
  const rows = tiers.filter((t) => t.kind === kind);
  const last = rows.reduce((max, t) => Math.max(max, t.rankTo || t.rankFrom), 0);

  const remove = async (t) => {
    const yes = await confirm({
      title: "Xoá mốc thưởng",
      message: `Xoá mốc ${t.text}? Muốn ngừng tạm thời thì tắt "Đang áp dụng" thay vì xoá.`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.del(`/api/admin/arena-event/season/rewards/${t.id}`),
      (text) => text
    );
    if (ok) onChanged();
  };

  return (
    <PageCard
      title="Mốc thưởng mùa"
      subtitle="Gửi một thư cho mỗi người lúc chốt mùa (hạn thư 14 ngày)"
      color="warning"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => setEditing("new")}>
            <Icon>add</Icon>&nbsp;Thêm mốc
          </MDButton>
        )
      }
    >
      <MDBox px={2}>
        <Tabs value={kind} onChange={(e, v) => setKind(v)}>
          <Tab value={0} label={KIND_NAMES[0]} />
          <Tab value={1} label={KIND_NAMES[1]} />
        </Tabs>
      </MDBox>
      <AdminTable
        rows={rows}
        emptyText="Chưa có mốc nào"
        columns={[
          {
            key: "text",
            label: kind === 1 ? "Điều kiện" : "Khoảng hạng",
            render: (t) => (
              <MDTypography variant="button" fontWeight="medium">
                {t.text}
              </MDTypography>
            ),
          },
          { key: "gift", label: "Quà", wrap: true, render: (t) => <GiftPreview lines={t.lines} /> },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (t) => (
              <Chip
                size="small"
                color={t.enabled ? "success" : "default"}
                label={t.enabled ? "Đang áp dụng" : "Tắt"}
              />
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (t) => (
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton size="small" variant="text" color="warning" onClick={() => setEditing(t)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
                <MDButton size="small" variant="text" color="error" onClick={() => remove(t)}>
                  <Icon>delete</Icon>
                </MDButton>
              </MDBox>
            ),
          },
        ].filter(Boolean)}
      />
      {editing && (
        <SeasonTierDialog
          tier={editing === "new" ? null : editing}
          kind={kind}
          nextFrom={last + 1}
          onClose={() => setEditing(null)}
          onDone={onChanged}
        />
      )}
    </PageCard>
  );
}

TiersCard.propTypes = {
  tiers: PropTypes.arrayOf(PropTypes.object).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function Info({ label, children }) {
  return (
    <MDBox display="flex" justifyContent="space-between" gap={2} py={0.5}>
      <MDTypography variant="button" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium" textAlign="right">
        {children}
      </MDTypography>
    </MDBox>
  );
}

Info.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

/** Mùa lôi đài: chọn mùa, BXH mùa (kèm thưởng dự kiến / đã chốt), điều kiện và mốc thưởng */
function SeasonPanel({ canEdit }) {
  const [seasonId, setSeasonId] = useState(0);
  const season = useFetch(
    () => api.get(`/api/admin/arena-event/season?seasonId=${seasonId}`),
    [seasonId]
  );
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [minMatches, setMinMatches] = useState(10);
  const [rewardEnabled, setRewardEnabled] = useState(true);
  const data = season.data;

  useEffect(() => {
    if (data) {
      setMinMatches(data.config.minMatches);
      setRewardEnabled(data.config.rewardEnabled);
    }
  }, [data?.config.minMatches, data?.config.rewardEnabled]);

  const saveConfig = async () => {
    const yes = await confirm({
      title: "Lưu điều kiện thưởng mùa",
      message: [
        `Số trận tối thiểu để nhận thưởng hạng: ${data.config.minMatches} → ${Number(minMatches)}.`,
        `Tự chốt thưởng khi hết mùa: ${data.config.rewardEnabled ? "bật" : "tắt"} → ${
          rewardEnabled ? "bật" : "tắt"
        }.`,
        !rewardEnabled
          ? "\nĐang TẮT tự chốt: hết mùa người chơi sẽ chưa nhận thưởng cho tới khi bật lại."
          : null,
      ]
        .filter(Boolean)
        .join("\n"),
      color: rewardEnabled ? "info" : "warning",
      confirmText: "Lưu điều kiện",
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put("/api/admin/arena-event/season/config", {
          minMatches: Number(minMatches),
          rewardEnabled,
        }),
      (text) => text
    );
    if (ok) season.reload(true);
  };

  const resend = async () => {
    const yes = await confirm({
      title: "Gửi lại thư thưởng còn thiếu",
      message:
        "Máy chủ chốt mùa đang chờ chốt (nếu có) và gửi những thư thưởng chưa tới tay người chơi (ví dụ lúc chốt máy chủ bị tắt). Thư đã gửi không gửi lại.",
      confirmText: "Gửi thư còn thiếu",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.post("/api/admin/arena-event/season/resend"),
      (text) => text
    );
    if (ok) setTimeout(() => season.reload(true), 4000);
  };

  const current = data?.season;
  const settled = Boolean(current?.settled);
  const pending = (data?.ranking || []).some((r) => r.pendingMail);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={4}>
        <PageCard
          title="Mùa lôi đài"
          subtitle="14 ngày, kết thúc 23:30 Chủ nhật"
          color="primary"
          actions={
            <MDButton size="small" color="white" onClick={() => season.reload()}>
              <Icon>refresh</Icon>
            </MDButton>
          }
        >
          {season.error && <Alert severity="error">{season.error}</Alert>}
          {data && (
            <MDBox>
              <MDInput
                select
                fullWidth
                label="Mùa"
                value={current?.id || 0}
                onChange={(e) => setSeasonId(Number(e.target.value))}
                SelectProps={{ sx: { height: 40 } }}
              >
                {data.seasons.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    Mùa {s.id}
                    {s.id === data.currentSeasonId ? " (hiện tại)" : ""} · {s.statusName}
                  </MenuItem>
                ))}
              </MDInput>
              {current && (
                <MDBox mt={2}>
                  <MDBox mb={1}>
                    <Chip
                      color={STATUS_COLORS[current.status] || "default"}
                      label={current.statusName}
                    />
                  </MDBox>
                  <Info label="Bắt đầu">{formatDate(current.startAt)}</Info>
                  <Info label="Kết thúc">{formatDate(current.endAt)}</Info>
                  <Info label="Người có điểm">{formatNumber(data.total)}</Info>
                  {settled && (
                    <Info label="Người nhận thưởng">
                      {formatNumber(data.seasons.find((s) => s.id === current.id)?.rewarded || 0)}
                    </Info>
                  )}
                </MDBox>
              )}
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                Điều kiện thưởng
              </MDTypography>
              <MDInput
                fullWidth
                type="number"
                label="Số trận tối thiểu để nhận thưởng hạng"
                value={minMatches}
                disabled={!canEdit}
                inputProps={{ min: 0 }}
                onChange={(e) => setMinMatches(e.target.value)}
              />
              <FormControlLabel
                sx={{ ml: 0, mt: 1 }}
                control={
                  <Switch
                    checked={rewardEnabled}
                    disabled={!canEdit}
                    onChange={(e) => setRewardEnabled(e.target.checked)}
                  />
                }
                label="Tự chốt thưởng khi hết mùa"
              />
              <MDTypography variant="caption" color="text" display="block">
                Tắt thì mùa vẫn sang mùa mới đúng giờ, mùa cũ chờ chốt tới khi bật lại (dùng bảng
                hạng đã lưu của mùa đó).
              </MDTypography>
              {canEdit && (
                <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    disabled={busy}
                    onClick={saveConfig}
                  >
                    <Icon>save</Icon>&nbsp;Lưu điều kiện
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color={pending ? "error" : "secondary"}
                    size="small"
                    disabled={busy}
                    onClick={resend}
                  >
                    <Icon>send</Icon>&nbsp;Chốt / gửi thư còn thiếu
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          )}
        </PageCard>
      </Grid>
      <Grid item xs={12} lg={8}>
        <PageCard
          title={current ? `BXH mùa ${current.id}` : "BXH mùa"}
          subtitle={
            settled
              ? "Quà đã chốt và trạng thái thư của từng người"
              : `Thưởng dự kiến theo mốc đang áp dụng, cần ít nhất ${
                  data?.config.minMatches ?? 0
                } trận để nhận thưởng hạng`
          }
          color="info"
          noPadding
        >
          <AdminTable
            loading={season.loading && !data}
            rows={data?.ranking || []}
            rowKey="userId"
            emptyText="Mùa này chưa ai có điểm"
            columns={[
              { key: "rank", label: "Hạng", align: "center", render: (r) => `#${r.rank}` },
              {
                key: "name",
                label: "Người chơi",
                render: (r) => (
                  <Link to={`/players/${r.userId}`}>{r.name || `Tài khoản ${r.userId}`}</Link>
                ),
              },
              {
                key: "points",
                label: "Điểm mùa",
                align: "center",
                render: (r) => formatNumber(r.points),
              },
              {
                key: "matches",
                label: "Trận",
                align: "center",
                render: (r) => (
                  <MDBox>
                    {r.wins} thắng, {r.losses} thua
                    {!r.enoughMatches && (
                      <MDTypography variant="caption" color="warning" display="block">
                        chưa đủ trận
                      </MDTypography>
                    )}
                  </MDBox>
                ),
              },
              {
                key: "reward",
                label: settled ? "Quà đã chốt" : "Thưởng dự kiến",
                wrap: true,
                render: (r) =>
                  settled ? (
                    <MDBox>
                      {r.giftLines?.length ? <GiftPreview lines={r.giftLines} /> : "-"}
                      {r.noRankMatches && (
                        <MDTypography variant="caption" color="warning" display="block">
                          Không nhận thưởng hạng vì chưa đủ trận
                        </MDTypography>
                      )}
                      {r.giftLines?.length > 0 && (
                        <Chip
                          size="small"
                          sx={{ mt: 0.5 }}
                          color={r.pendingMail ? "error" : "success"}
                          label={
                            r.pendingMail ? "Chưa gửi được thư" : `Đã gửi ${formatDate(r.sentAt)}`
                          }
                        />
                      )}
                    </MDBox>
                  ) : (
                    <MDBox>
                      {r.rankTier && (
                        <MDTypography
                          variant="caption"
                          display="block"
                          color={r.noRankMatches ? "warning" : "dark"}
                          sx={r.noRankMatches ? { textDecoration: "line-through" } : undefined}
                        >
                          {r.rankTier}
                        </MDTypography>
                      )}
                      {r.pointsTier && (
                        <MDTypography variant="caption" display="block">
                          Mốc tham gia {r.pointsTier}
                        </MDTypography>
                      )}
                      {!r.rankTier && !r.pointsTier && "-"}
                    </MDBox>
                  ),
              },
            ]}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12}>
        {data && (
          <TiersCard tiers={data.tiers} canEdit={canEdit} onChanged={() => season.reload(true)} />
        )}
      </Grid>
    </Grid>
  );
}

SeasonPanel.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default SeasonPanel;
